import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './TechStackModal.css';

function TechStackModal({ isOpen, onClose }) {
  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="techstack-overlay" onClick={onClose}>
      <div className="techstack-content" onClick={(e) => e.stopPropagation()}>
        <button className="techstack-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="techstack-header">
          <h2 className="techstack-title">🚀 Bot Architecture</h2>
          <p className="techstack-subtitle">Ein Blick in meinen digitalen Assistenten</p>
        </div>

        <div className="workflow-container">
          {/* Step 1: User Input */}
          <div className="workflow-step" data-step="1">
            <div className="step-icon user-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>User Input</h3>
            <p>Ihre Frage wird eingegeben</p>
            <div className="tech-tags">
              <span className="tech-tag">React</span>
              <span className="tech-tag">TypeScript</span>
            </div>
          </div>

          <div className="workflow-arrow">→</div>

          {/* Step 2: Frontend */}
          <div className="workflow-step" data-step="2">
            <div className="step-icon frontend-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 9h18" stroke="currentColor" strokeWidth="2"/>
                <path d="M9 21V9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Frontend</h3>
            <p>UI & State Management</p>
            <div className="tech-tags">
              <span className="tech-tag">React.js</span>
              <span className="tech-tag">CSS3</span>
            </div>
          </div>

          <div className="workflow-arrow">→</div>

          {/* Step 3: Backend API */}
          <div className="workflow-step" data-step="3">
            <div className="step-icon backend-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 1v6m0 6v6M23 12h-6m-6 0H1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Backend API</h3>
            <p>Request Processing</p>
            <div className="tech-tags">
              <span className="tech-tag">FastAPI</span>
              <span className="tech-tag">Python</span>
            </div>
          </div>

          <div className="workflow-arrow">→</div>

          {/* Step 4: Vector Search */}
          <div className="workflow-step" data-step="4">
            <div className="step-icon database-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Vector Search</h3>
            <p>Knowledge Base Retrieval</p>
            <div className="tech-tags">
              <span className="tech-tag">ChromaDB</span>
              <span className="tech-tag">Embeddings</span>
            </div>
          </div>

          <div className="workflow-arrow">→</div>

          {/* Step 5: LLM Processing */}
          <div className="workflow-step" data-step="5">
            <div className="step-icon ai-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>LLM Processing</h3>
            <p>AI Response Generation</p>
            <div className="tech-tags">
              <span className="tech-tag">GPT-4o-mini</span>
              <span className="tech-tag">LangChain</span>
            </div>
          </div>

          <div className="workflow-arrow">→</div>

          {/* Step 6: Response */}
          <div className="workflow-step" data-step="6">
            <div className="step-icon response-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Response</h3>
            <p>Antwort wird angezeigt</p>
            <div className="tech-tags">
              <span className="tech-tag">Markdown</span>
              <span className="tech-tag">Real-time</span>
            </div>
          </div>
        </div>

        <div className="techstack-footer">
          <div className="deployment-info">
            <div className="deployment-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2"/>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Backend: Railway</span>
            </div>
            <div className="deployment-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2"/>
                <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Frontend: Firebase Hosting</span>
            </div>
          </div>
          <div className="cost-info">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Kosten pro Anfrage: ~0.2 Cent<br />(98% günstiger durch Optimierung)</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TechStackModal;
